import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MagnifyingGlass, 
  Clock, 
  BookOpen,
  Lock,
  ArrowRight,
  Spinner,
  Funnel
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function toDirectImageUrl(url) {
  if (!url) return url;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return `https://drive.google.com/thumbnail?id=${driveMatch[1]}&sz=w1280`;
  const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (driveOpenMatch) return `https://drive.google.com/thumbnail?id=${driveOpenMatch[1]}&sz=w1280`;
  const driveUcMatch = url.match(/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (driveUcMatch) return `https://drive.google.com/thumbnail?id=${driveUcMatch[1]}&sz=w1280`;
  return url;
}

export function Courses() {
  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/courses/products`).catch(() => ({ data: [] }))
      ]);
      setCourses(coursesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = !searchTerm || 
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = !selectedProduct || course.product === selectedProduct;
    return matchesSearch && matchesProduct;
  });

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#27AE60] mb-2">
            Catalogo de Cursos
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A252F] mb-4">
            Explore Nossos Cursos de Treinamento
          </h1>
          <p className="text-[#5C6B7A] max-w-2xl">
            Navegue pela nossa colecao completa de cursos desenvolvidos para ajuda-lo a dominar os produtos e servicos da Tecnoservice.
          </p>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
            <Input
              type="text"
              placeholder="Pesquisar cursos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50]"
              data-testid="course-search"
            />
          </div>

          {products.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Funnel size={18} className="text-[#5C6B7A] flex-shrink-0" />
              <button
                onClick={() => setSelectedProduct('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  !selectedProduct
                    ? 'bg-[#2C3E50] text-white'
                    : 'bg-white border border-[#BDC3C7] text-[#5C6B7A] hover:border-[#2C3E50]'
                }`}
                data-testid="filter-all"
              >
                Todos
              </button>
              {products.map(product => (
                <button
                  key={product}
                  onClick={() => setSelectedProduct(selectedProduct === product ? '' : product)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedProduct === product
                      ? 'bg-[#2C3E50] text-white'
                      : 'bg-white border border-[#BDC3C7] text-[#5C6B7A] hover:border-[#2C3E50]'
                  }`}
                  data-testid={`filter-${product}`}
                >
                  {product}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size={48} className="animate-spin text-[#2C3E50]" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen size={64} className="mx-auto text-[#BDC3C7] mb-4" />
            <h3 className="text-xl font-semibold text-[#1A252F] mb-2">Nenhum curso encontrado</h3>
            <p className="text-[#5C6B7A]">Tente ajustar seus termos de pesquisa</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course) => (
              <div 
                key={course.id}
                className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                data-testid={`course-card-${course.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={toDirectImageUrl(course.thumbnail_url) || 'https://images.unsplash.com/photo-1765438868883-43efce4fd145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHNlc3Npb258ZW58MHx8fHwxNzc1MzUxNTk2fDA&ixlib=rb-4.1.0&q=85'}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  {course.product && (
                    <div className="absolute top-3 left-3 bg-[#2C3E50] text-white px-3 py-1 rounded-full text-xs font-medium">
                      {course.product}
                    </div>
                  )}
                  {course.client_access?.length > 0 && (
                    <div className="absolute top-3 right-3 bg-white/90 text-[#2C3E50] px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Lock size={12} />
                      Exclusivo
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#1A252F] mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  <p className="text-[#5C6B7A] text-sm mb-4 line-clamp-2">
                    {course.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-[#5C6B7A] mb-4">
                    <div className="flex items-center gap-1">
                      <Clock size={16} />
                      <span>{course.estimated_duration} min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen size={16} />
                      <span>{course.module_count} modulos</span>
                    </div>
                  </div>

                  <Link to={`/courses/${course.id}`}>
                    <Button 
                      className="w-full bg-[#2C3E50] text-white hover:bg-[#1A252F] font-medium"
                      data-testid={`view-course-${course.id}`}
                    >
                      Ver Curso
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Courses;
